<template>
  <div class="bm-stranded" :data-testid="`stranded-orders-${batchId}`">
    <div v-if="refundsOnly" class="bm-stranded__head">
      <i class="material-icons bm-stranded__icon" aria-hidden="true">report_problem</i>
      <div>
        <!--
          THE CLOSED-BATCH FORM (option A). The batch was abandoned and is
          released; what is left is the operator's to-do list. Each order
          leaves it on its own once its refund is recorded.
        -->
        <b>
          {{ items.length }} {{ items.length === 1 ? 'order still needs' : 'orders still need' }}
          a refund.
        </b>
        This batch is closed. Refund each shopper, and let them know why --
        the message below is ready to copy. An order drops off this list once
        its refund goes through.
      </div>
    </div>
    <div v-else class="bm-stranded__head">
      <i class="material-icons bm-stranded__icon" aria-hidden="true">report_problem</i>
      <div>
        <b>
          {{ chargedCount }} {{ chargedCount === 1 ? 'shopper was' : 'shoppers were' }}
          charged for {{ chargedCount === 1 ? 'an order' : 'orders' }} the decorator
          never received.
        </b>
        That is {{ money(chargedTotalDollars) }} collected for
        {{ chargedCount === 1 ? 'work that is' : 'work that is' }} not in production.
        Nothing here resolves on its own.
      </div>
    </div>

    <div
      v-for="it in items"
      :key="it.id"
      class="bm-stranded__item"
      :data-testid="`stranded-item-${it.id}`"
    >
      <div class="bm-stranded__who">
        <div class="bm-stranded__order">{{ it.orderLabel }}</div>
        <!--
          THE MONEY LINE, and the `held_back` disambiguation lives here.
          `held_back` means two different things: at charge time the card was
          declined and NO money moved; at submit time the shopper WAS charged
          and the decorator never got the order. `chargedCents` is the
          discriminator, so the two render as different sentences rather than
          one hedge covering both.
        -->
        <div v-if="it.chargedCents > 0" class="bm-stranded__money">
          <b>{{ money(it.chargedCents / 100) }}</b> charged
          <span v-if="it.chargedAtLabel"> &middot; {{ it.chargedAtLabel }}</span>
        </div>
        <div v-else class="bm-stranded__money bm-stranded__money--none">
          No money was taken for this one.
        </div>
      </div>

      <div class="bm-stranded__why">
        <!--
          A plain sentence when the API has one, else the raw token, else
          NOTHING. A null error used to print "unknown", which on a delivered
          order was a false alarm and on a live one was an invented cause.
        -->
        <span
          v-if="it.lastErrorSummary"
          class="bm-stranded__summary"
          :data-testid="`stranded-error-${it.id}`"
        >{{ it.lastErrorSummary }}</span>
        <span
          v-else-if="it.lastError"
          class="bm-stranded__token"
          :data-testid="`stranded-error-${it.id}`"
        >{{ it.lastError }}</span>
        <span class="bm-stranded__attempts">
          {{ it.attempts }} {{ it.attempts === 1 ? 'attempt' : 'attempts' }}
        </span>
        <!--
          EXHAUSTED VERSUS STILL TRYING, as two distinct statements rather
          than the presence or absence of one. A batch still retrying needs
          no decision yet; one that has stopped does. Saying nothing in the
          still-trying case would read as "stopped" to anyone who had seen
          the other copy.
        -->
        <span v-if="it.exhausted" class="bm-stranded__exhausted">
          Stopped retrying &mdash; this one needs you.
        </span>
        <!--
          THREE statements, not two (#424). `retryable` is the server's word
          that the drain will pick this item up again; an item that is
          neither exhausted nor retryable is PARKED -- a decorator contract
          refusal is permanent and goes straight to held_back with attempts
          still at 1, and it read "Still trying automatically" for an order
          nothing was going to retry.
        -->
        <span v-else-if="it.retryable" class="bm-stranded__trying">
          Still trying automatically.
        </span>
        <span
          v-else
          class="bm-stranded__parked"
          :data-testid="`stranded-parked-${it.id}`"
        >
          Will not be retried automatically &mdash; this one needs you.
        </span>
        <span
          v-if="it.refundNeeded"
          class="bm-stranded__refund"
          :data-testid="`refund-needed-${it.id}`"
        >
          You abandoned this one &mdash; it needs a manual refund.
        </span>
      </div>

      <div class="bm-stranded__actions">
        <!--
          Offered ONLY when the API will accept it. `ui_retry_release_item`
          409s anything outside its claimable states, and on a `done` item a
          retry would submit a SECOND real order for goods already in
          production. `retryable` is computed server-side and passed through
          rather than re-derived here, so there is one copy of that rule.
        -->
        <button
          v-if="it.retryable"
          type="button"
          class="bm-btn bm-btn--sm"
          :data-testid="`retry-item-${it.id}`"
          :disabled="busyItemIds.includes(it.id)"
          @click="$emit('retry-item', it)"
        >
          <i class="material-icons bm-btn__icon" aria-hidden="true">refresh</i>
          Try this one again
        </button>

        <!--
          THE WAY OUT OF EXHAUSTION, and the reason this component exists in
          its current form. On 2026-09-25 batch B-31 sat blocked over a
          CHARGED, never-delivered order whose item had spent all six attempts
          against a back-office batch id that had been minted under the other
          mode and 404'd. #3807 now detects and re-targets exactly that -- but
          the item was already terminal, so `retryable` was false and the ONLY
          control this screen offered was "Give up on these orders and close
          the batch": over the shopper's money, no refund, never delivered.
          Unsticking it took a hand-written database UPDATE.

          So this button is not a convenience. Without it the destructive
          action is the only action, which is the single user-facing outcome
          the follow-up exists to prevent.

          `rearmable` is computed SERVER-SIDE and passed through, exactly like
          `retryable` beside it: the backend owns which states its route
          accepts, and a second copy of that rule here is the copy that
          drifts. The two are mutually exclusive in practice -- a live item is
          retryable, a terminal one is rearmable -- but neither is derived from
          the other, because that inference is the backend's to make.

          The copy says what actually happens ("try it again", not "fix it"):
          the re-arm puts the order back in the queue and the next attempt
          decides. A genuinely dead order spends its budget again and returns
          here, which is correct and is why "Give up" stays.
        -->
        <button
          v-if="it.rearmable"
          type="button"
          class="bm-btn bm-btn--sm"
          :data-testid="`rearm-item-${it.id}`"
          :disabled="busyItemIds.includes(it.id)"
          @click="$emit('rearm-item', it)"
        >
          <i class="material-icons bm-btn__icon" aria-hidden="true">replay</i>
          Try this one again
        </button>
      </div>

      <!--
        OPTION B (Jake, 2026-09-28): the operator abandoned this CHARGED order,
        so its shopper is owed an explanation and a refund. Keyed on the same
        server-served `refundNeeded` flag as the line above -- NOT on
        `chargedCents` -- because a stranded order still being retried may yet
        ship, and a message promising a refund would pre-empt that.
      -->
      <AbandonedShopperMessage v-if="it.refundNeeded" :item="it" />
    </div>
  </div>
</template>

<script>
/**
 * The stranded-orders detail: which shopper, how much, since when, why.
 *
 * Design 2026-09-15-batch-release-architecture-recommendation.md section 6.8.
 * Jake, 2026-09-15: *"make sure the UI reflects the issue. A human will need
 * to be involved here. It's a trust-buster to cancel/refund an order for
 * reasons that won't make sense to an average shopper."*
 *
 * A batch parked in `release_blocked` that only a SQL query can find is not
 * "a human decides", it is "a human never finds out". This component is the
 * difference. It renders facts and offers the one non-destructive action;
 * the destructive one (abandon) stays on the row, next to the batch it
 * closes.
 */
import { money } from "@/services/batchManagementService";
import AbandonedShopperMessage from "./AbandonedShopperMessage.vue";

export default {
  name: "StrandedOrders",
  components: { AbandonedShopperMessage },
  props: {
    batchId: { type: [String, Number], required: true },
    items: { type: Array, default: () => [] },
    // Item ids with a retry in flight. Owned by the panel, which owns the
    // request, so this component stays a pure function of its props.
    busyItemIds: { type: Array, default: () => [] },
    // The batch is released and these are the orders still owed a refund
    // (option A). Changes the heading from "stranded" to "refund owed"; the
    // items themselves render the same way.
    refundsOnly: { type: Boolean, default: false },
  },
  // `rearm-item` is DISTINCT from `retry-item`: the first revives a terminal
  // item, the second nudges a live one. Separate events so the panel's two
  // handlers hit their own routes and the audit trail can tell them apart.
  emits: ["retry-item", "rearm-item"],
  computed: {
    /** Items that actually hold money. The `held_back` discriminator. */
    chargedItems() {
      return this.items.filter((i) => i.chargedCents > 0);
    },
    chargedCount() {
      return this.chargedItems.length;
    },
    chargedTotalDollars() {
      return (
        this.chargedItems.reduce((sum, i) => sum + i.chargedCents, 0) / 100
      );
    },
  },
  methods: { money },
};
</script>

<style scoped>
.bm-stranded {
  border: 1px solid var(--tf-error);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 12px 0;
  background: rgba(200, 42, 42, 0.04);
}

.bm-stranded__head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 10px;
}

.bm-stranded__icon {
  color: var(--tf-error);
  font-size: 20px;
}

.bm-stranded__item {
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(200px, 2fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid var(--tf-border);
  font-size: 13px;
}

.bm-stranded__order {
  font-weight: 600;
}

.bm-stranded__money--none {
  color: var(--tf-text-muted, #6b7785);
}

.bm-stranded__why {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  align-items: center;
}

.bm-stranded__token {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  background: var(--tf-surface-alt, #f1f4f7);
  border-radius: 4px;
  padding: 1px 6px;
}

.bm-stranded__attempts,
.bm-stranded__trying {
  color: var(--tf-text-muted, #6b7785);
}

.bm-stranded__exhausted,
.bm-stranded__refund {
  color: var(--tf-error);
  font-weight: 600;
}

.bm-stranded__actions {
  justify-self: end;
}
</style>
